import type {Rule} from 'antd/es/form';
import moment from 'moment';

import {
  isOtherStoredValue,
  labelFromOtherStored,
  selectWithOtherBusinessNameRule
} from '../../../components/CustomInputs/Select/SelectWithOtherOption';
import {CommonErrors} from '../../../constants/Common';
import {validatePCF} from '../../../services/modules/PCF/pcfAPI';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import RegEx from '../../../utils/FormUtils/RegEx';
import {yearSelect} from '../../../utils/FormUtils/yearSelect';
import {isDateWithinFinancialYear} from '../../esgManager/utils/financialYearDateValidation';
import {CBAM_APPLICATION_VALUE} from '../constants/pcfOptions';
import {GoalAndScopeLabels} from '../PCFKeyLabels';

import {
  financialYearStartYear,
  isValidReferenceYearPair,
  normalizeReferenceYearValue
} from './referenceYearUtils';

export const PCF_STUDY_DATE_FORMAT = 'DD/MM/YYYY';
export const PCF_STUDY_DATE_PARSE_FORMATS = [
  PCF_STUDY_DATE_FORMAT,
  'DD-MM-YYYY',
  'YYYY-MM-DD'
] as const;

export const parseStudyDateValue = (
  value: string | moment.Moment
): moment.Moment | undefined => {
  if (moment.isMoment(value)) {
    return value.isValid() ? value : undefined;
  }

  const trimmed = value?.trim();

  if (!trimmed) {
    return undefined;
  }

  const parsed = moment(trimmed, [...PCF_STUDY_DATE_PARSE_FORMATS], true);

  return parsed.isValid() ? parsed : undefined;
};

export const getFinancialYearForStudyDate = (
  date: moment.Moment
): string | undefined => {
  const fiscalYearPreference =
    useCompanyStore.getState().preUnits?.financialYear;

  return yearSelect(fiscalYearPreference).find((option) =>
    isDateWithinFinancialYear(date, fiscalYearPreference, option.value)
  )?.value;
};

export const studyNamePattern = /^[A-Za-z0-9 -]+$/;

export interface DuplicateStudyNameCheckResult {
  isDuplicate: boolean;
  message?: string;
}

export const checkDuplicateStudyName = async (
  name: string,
  companyId: string,
  excludeId?: string
): Promise<DuplicateStudyNameCheckResult> => {
  const trimmed = name.trim();
  if (!trimmed || !companyId) {
    return {isDuplicate: false};
  }

  try {
    const response = await validatePCF({
      company: companyId,
      ...(excludeId ? {excludeId} : {}),
      checks: [{key: 'name', value: trimmed}]
    });

    if (response.data == null || response.data.valid) {
      return {isDuplicate: false};
    }

    const nameResult = response.data.results?.find((r) => r.key === 'name');

    return {
      isDuplicate: true,
      message: nameResult?.message
    };
  } catch {
    return {isDuplicate: false};
  }
};

export const studyNameRules: Rule[] = [
  {
    required: true,
    whitespace: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.STUDY_NAME)
  },
  {
    pattern: studyNamePattern,
    message: 'Only letters, numbers, spaces, and hyphens are allowed'
  }
];

export const productNameRules: Rule[] = [
  {
    required: true,
    whitespace: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.PRODUCT_NAME)
  }
];

export const productCodeRules: Rule[] = [
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (value == null || String(value).trim() === '') {
        await Promise.resolve();

        return;
      }

      if (!RegEx.alphaNumeric.test(String(value).trim())) {
        throw new Error(
          CommonErrors.validField(GoalAndScopeLabels.PRODUCT_CODE)
        );
      }

      await Promise.resolve();
    }
  }
];

export const industrySectorRules: Rule[] = [
  {
    required: true,
    type: 'array',
    min: 1,
    message: CommonErrors.mandatory(GoalAndScopeLabels.INDUSTRY_SECTOR)
  },
  selectWithOtherBusinessNameRule()
];

export const reportingOrgRules: Rule[] = [
  {
    required: true,
    whitespace: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.REPORTING_ORG)
  },
  {
    max: 200,
    message: CommonErrors.notMoreThan(GoalAndScopeLabels.REPORTING_ORG, 200)
  }
];

export const studyDateRules: Rule[] = [
  {
    required: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.STUDY_DATE)
  },
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (value == null || String(value).trim() === '') {
        await Promise.resolve();

        return;
      }

      const parsed = parseStudyDateValue(String(value));

      if (!parsed) {
        throw new Error('Enter a valid date');
      }

      if (parsed.isAfter(moment(), 'day')) {
        throw new Error('Study Date cannot be in the future');
      }

      await Promise.resolve();
    }
  }
];

export const referenceYearRules = (
  getStudyDate: () => string | undefined
): Rule[] => [
  {
    required: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.REFERENCE_YEAR)
  },
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      const normalized = normalizeReferenceYearValue(value);

      if (!normalized?.[0]?.trim() || !normalized?.[1]?.trim()) {
        await Promise.resolve();

        return;
      }

      const startYear = parseInt(normalized[0], 10);
      const endYear = parseInt(normalized[1], 10);

      if (!Number.isNaN(startYear) && !Number.isNaN(endYear) && endYear < startYear) {
        throw new Error(
          'End year must be the same as or after start year'
        );
      }

      const currentYear = moment().year();

      if (!Number.isNaN(startYear) && startYear > currentYear) {
        throw new Error('Start year cannot be in the future');
      }

      if (!Number.isNaN(endYear) && endYear > currentYear) {
        throw new Error('End year cannot be in the future');
      }

      if (!isValidReferenceYearPair(normalized[0], normalized[1])) {
        throw new Error(
          'Study Reference must use valid calendar years (e.g. 2024 to 2026)'
        );
      }

      const studyDateRaw = getStudyDate();

      if (!studyDateRaw) {
        await Promise.resolve();

        return;
      }

      const studyMoment = parseStudyDateValue(studyDateRaw);

      if (!studyMoment) {
        await Promise.resolve();

        return;
      }

      const studyFinancialYear = getFinancialYearForStudyDate(studyMoment);

      if (!studyFinancialYear) {
        await Promise.resolve();

        return;
      }

      const studyFinancialYearStart =
        financialYearStartYear(studyFinancialYear);

      if (startYear < studyFinancialYearStart) {
        throw new Error(
          'Study Reference must be the same as or after the financial year that contains the Study Date'
        );
      }

      await Promise.resolve();
    }
  }
];

export const hsnCodeRules: Rule[] = [
  {
    required: true,
    type: 'array',
    min: 1,
    message: CommonErrors.mandatory(GoalAndScopeLabels.HS_CODE)
  }
];

export const intendedApplicationRules: Rule[] = [
  {
    required: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.INTENDED_APPLICATION)
  }
];

export const productCNCodeRules = (
  getIntendedApplication: () => string | undefined
): Rule[] => [
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      const app = getIntendedApplication();
      if (
        app === CBAM_APPLICATION_VALUE &&
        !(value != null && String(value).trim())
      ) {
        throw new Error(
          CommonErrors.mandatory(GoalAndScopeLabels.PRODUCT_CN_CODE)
        );
      }

      await Promise.resolve();
    }
  }
];

export const pcfStandardRules: Rule[] = [
  {
    required: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.PCF_STANDARD)
  }
];

export const audienceSelectionRules: Rule[] = [
  {
    required: true,
    type: 'array',
    min: 1,
    message: CommonErrors.mandatory(GoalAndScopeLabels.INTENDED_AUDIENCES)
  }
];

const selectWithOtherStoredText = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '';
  }

  return isOtherStoredValue(raw) ? labelFromOtherStored(raw).trim() : raw;
};

const audienceOtherText = selectWithOtherStoredText;

const countDecimalPlaces = (value: number): number => {
  const [, fraction = ''] = String(value).split('.');

  return fraction.length;
};

export const audienceOtherRules = (
  getAudienceSelection: () => string[] | undefined
): Rule[] => [
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      const selection = getAudienceSelection();
      if (!selection?.includes('other')) {
        await Promise.resolve();

        return;
      }

      const text = audienceOtherText(value);

      if (!text) {
        throw new Error(
          CommonErrors.mandatory(GoalAndScopeLabels.AUDIENCE_OTHER)
        );
      }

      if (text.length > 80) {
        throw new Error(CommonErrors.notMoreThan(GoalAndScopeLabels.AUDIENCE_OTHER, 80));
      }

      await Promise.resolve();
    }
  }
];

export const fuQuantityRules: Rule[] = [
  {
    required: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.FU_QUANTITY)
  },
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (value == null || value === '') {
        throw new Error(
          `${GoalAndScopeLabels.FU_QUANTITY} must be greater than 0`
        );
      }

      const n = typeof value === 'number' ? value : parseFloat(String(value));

      if (Number.isNaN(n) || n <= 0) {
        throw new Error(
          `${GoalAndScopeLabels.FU_QUANTITY} must be greater than 0`
        );
      }

      if (countDecimalPlaces(n) > 4) {
        throw new Error(
          `${GoalAndScopeLabels.FU_QUANTITY} allows up to 4 decimal places`
        );
      }

      await Promise.resolve();
    }
  }
];

export const fuUnitRules: Rule[] = [
  {
    required: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.FU_UNIT)
  }
];

export const fuUnitOtherRules = (
  getFuUnit: () => string | undefined
): Rule[] => [
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (getFuUnit() !== 'other_specify') {
        await Promise.resolve();

        return;
      }

      const text = selectWithOtherStoredText(value);

      if (!text) {
        throw new Error(
          CommonErrors.mandatory(GoalAndScopeLabels.FU_UNIT_OTHER)
        );
      }

      if (text.length > 20) {
        throw new Error(
          CommonErrors.notMoreThan(GoalAndScopeLabels.FU_UNIT_OTHER, 20)
        );
      }

      await Promise.resolve();
    }
  },
  {
    pattern: RegEx.businessName,
    message: CommonErrors.noAllowedSpecChar
  }
];

export const fuDescriptionRules: Rule[] = [
  {
    required: true,
    whitespace: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.FU_DESCRIPTION)
  },
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      const trimmed = value != null ? String(value).trim() : '';
      if (trimmed.length < 20) {
        throw new Error(
          `${GoalAndScopeLabels.FU_DESCRIPTION} requires a minimum of 20 characters`
        );
      }

      await Promise.resolve();
    }
  }
];

export const referenceFlowRules: Rule[] = [
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (value == null || String(value).trim() === '') {
        await Promise.resolve();

        return;
      }

      if (String(value).length > 200) {
        throw new Error(
          CommonErrors.notMoreThan(GoalAndScopeLabels.REFERENCE_FLOW, 200)
        );
      }

      await Promise.resolve();
    }
  }
];

export const exclusionsRules: Rule[] = [
  {
    validator: async (_: unknown, value: unknown): Promise<void> => {
      if (value == null || String(value).trim() === '') {
        await Promise.resolve();

        return;
      }

      if (String(value).length > 500) {
        throw new Error(
          CommonErrors.notMoreThan(GoalAndScopeLabels.EXCLUSIONS, 500)
        );
      }

      await Promise.resolve();
    }
  }
];

export const boundaryScopeRules: Rule[] = [
  {
    required: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.BOUNDARY_SCOPE)
  }
];

export const geographicScopeRules: Rule[] = [
  {
    required: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.GEOGRAPHIC_SCOPE)
  }
];

export const allocationMethodRules: Rule[] = [
  {
    required: true,
    message: CommonErrors.mandatory(GoalAndScopeLabels.ALLOCATION_METHOD)
  }
];
